import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import StatsGrid from '@/components/StatsGrid';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { statusColors, priorityColors, attorneyName } from '@/lib/caseFormatting';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, Briefcase, Calendar, User } from 'lucide-react';


const Cases = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [cases, setCases] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [stats, setStats] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [casesRes, attorneysRes, statsRes, clientsRes] = await Promise.all([
        api.get(`/cases`),
        api.get(`/attorneys`),
        api.get(`/cases/stats/summary`),
        api.get(`/clients`).catch(() => ({ data: [] })),
      ]);
      setCases(casesRes.data);
      setAttorneys(attorneysRes.data);
      setStats(statsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error('Failed to load cases');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner', 'associate'].includes(user.role)) {
        toast.error('Access denied. Attorney privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate, fetchData]);

  const deleteCase = async (id) => {
    if (!window.confirm('Are you sure you want to delete this case? All related dockets and time entries will also be deleted.')) return;
    try {
      await api.delete(`/cases/${id}`);
      toast.success('Case deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete case', error);
      toast.error('Failed to delete case');
    }
  };

  // The linked client's name is derived live from Client Management; fall back to
  // the snapshot stored on the case for any legacy/unlinked records.
  const clientName = (caseItem) =>
    clients.find((c) => c.id === caseItem.client_id)?.display_name || caseItem.client || '—';

  const filteredCases = cases.filter(c => {
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.case_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName(c).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Case Management"
        titleTestId="cases-title"
        actions={
          <Button onClick={() => navigate('/cases/new')} data-testid="new-case-btn">
            <Plus size={20} /> New Case
          </Button>
        }
      />

      {/* Stats Cards */}
      <StatsGrid cols={5}>
        <StatCard value={stats.total || 0} label="Total" onClick={() => setFilterStatus('all')} />
        <StatCard value={stats.open || 0} label="Open" valueClassName="text-blue-600" onClick={() => setFilterStatus('open')} />
        <StatCard value={stats.in_progress || 0} label="In Progress" valueClassName="text-yellow-600" onClick={() => setFilterStatus('in_progress')} />
        <StatCard value={stats.pending || 0} label="Pending" valueClassName="text-orange-600" onClick={() => setFilterStatus('pending')} />
        <StatCard value={stats.closed || 0} label="Closed" valueClassName="text-gray-600" onClick={() => setFilterStatus('closed')} />
      </StatsGrid>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <Input
          placeholder="Search cases..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="sm:w-80"
          data-testid="search-cases"
        />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="sm:w-48" data-testid="filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cases List */}
      <div className="space-y-4">
        {filteredCases.length === 0 ? (
          <EmptyState icon={Briefcase} message="No cases found" />
        ) : (
          filteredCases.map((caseItem) => (
            <Card key={caseItem.id} className="hover:shadow-md transition-shadow" data-testid={`case-card-${caseItem.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-sm text-accent font-medium">
                        {caseItem.case_number}
                      </span>
                      <Badge className={statusColors[caseItem.status]}>
                        {caseItem.status.replace('_', ' ')}
                      </Badge>
                      <Badge className={priorityColors[caseItem.priority]}>
                        {caseItem.priority}
                      </Badge>
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-primary mb-2">
                      {caseItem.title}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User size={14} /> {clientName(caseItem)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase size={14} /> {caseItem.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <User size={14} /> {attorneyName(attorneys, caseItem.assignee)}
                      </span>
                      {caseItem.due_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={14} /> {new Date(caseItem.due_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button asChild size="sm" variant="outline" data-testid={`view-case-${caseItem.id}`}>
                      <Link to={`/cases/${caseItem.id}`}>
                        <Eye size={16} /> View
                      </Link>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/cases/${caseItem.id}/edit`)}
                      data-testid={`edit-case-${caseItem.id}`}
                    >
                      <Edit size={16} /> Edit
                    </Button>
                    {user.role !== 'associate' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteCase(caseItem.id)}
                        data-testid={`delete-case-${caseItem.id}`}
                        aria-label={`Delete case ${caseItem.case_number}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </PageShell>
  );
};

export default Cases;
